'use client'

import React from 'react'
import { Phone, Mail } from 'lucide-react'
import { cn } from '@/utilities/ui'
import { formatPhoneNumber } from '@/utilities/formatPhoneNumber'
import { Agency, Property } from '@/payload-types'
import Image from 'next/image'
import { sendGTMEvent } from '@next/third-parties/google'

type PropertyContactInfoProps = {
  className?: string
  agency: Agency
  property: Property
  dictionary: any
}

const PropertyContactInfo = ({ className, property, agency, dictionary }: PropertyContactInfoProps) => {
  
  const handlePhoneClick = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
    
    sendGTMEvent({
      event: 'phone_click',
      phone_number: agency?.phone,
      property_id: property?.id,
    })

    window.open(`tel:${agency?.phone}`)
  }

  const handleWhatsappClick = (e: React.SyntheticEvent) => {
      e.stopPropagation()
      e.preventDefault()
  
      sendGTMEvent({
        event: 'whatsapp_click',
        whatsapp_number: agency?.contactSection?.whatsapp,
        property_id: property?.id,
      })
  
      window.open(
        `https://wa.me/${agency?.contactSection?.whatsapp}`,
        '_blank',
        'noopener,noreferrer'
      )
    }

  return (
    <div
      className={cn(
        'md:sticky md:top-[80px] h-fit bg-card container mx-auto rounded-xl shadow-sm p-[30px]',
        className,
      )}
    >
      <h2 className="text-typography text-[17px] font-bold mb-8">
        {dictionary?.propertyDetail?.getMoreInformation || 'Get More Information'}
      </h2>

      <div className="flex items-center sm:items-center gap-6 mb-8">
        {/* Agent Photo */}
        <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
          <Image
            src={agency?.Media?.thumbnailURL || agency?.Media?.url}
            alt={agency?.agencyDescriptionSection?.title}
            className="w-full h-full object-contain"
            width={100}
            height={100}
          />
        </div>

        {/* Agent Info */}
        <div>
          <h3 className="text-typography text-[15px] font-bold mb-2">
            {agency?.agencyDescriptionSection?.title}
          </h3>
          <div className="text-typography flex items-center gap-1">
            <Phone size={20} className="mr-2" />
            <a href={`tel:${agency?.phone}`} className="hover:text-primary-300">
              {formatPhoneNumber(agency?.phone, 'mobile')}
            </a>
          </div>
        </div>
      </div>

      {/* Contact Buttons */}
      <div className="grid grid-cols-2 gap-4">
        {agency?.phone && (
          <div
            onClick={handlePhoneClick}
            className="text-typography hover:text-black flex items-center justify-center gap-2 border border-gray-500 rounded-lg py-3 px-4 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <Phone size={20} />
            <span className="text-[15px] font-semibold">
              {dictionary?.propertyDetail?.call || 'Call'}
            </span>
          </div>
        )}

        {agency?.email && (
          <a
            href={`mailto:${agency?.email}`}
            className="text-typography hover:text-black flex items-center justify-center gap-2 border border-gray-500 rounded-lg py-3 px-4 hover:bg-gray-50 transition-colors"
          >
            <Mail size={20} />
            <span className="text-[15px] font-semibold">
              {dictionary?.propertyDetail?.email || 'Email'}
            </span>
          </a>
        )}

        {agency?.contactSection?.instagram && (
          <a
            href={`https://instagram.com/${agency?.contactSection?.instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-typography hover:text-black flex items-center justify-center gap-2 border border-gray-500 rounded-lg py-3 px-4 hover:bg-gray-50 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="#E1306C"
            >
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
            <span className="text-[15px] font-semibold">
              {dictionary?.propertyDetail?.instagram || 'Instagram'}
            </span>
          </a>
        )}

        {agency?.contactSection?.whatsapp && (
          <div
            onClick={handleWhatsappClick}
            className="text-typography hover:text-black flex items-center justify-center gap-2 border border-gray-500 rounded-lg py-3 px-4 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              viewBox="0 0 24 24"
              fill="#25D366"
            >
              <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.1.824zm-3.423-14.416c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm.029 18.88c-1.161 0-2.305-.292-3.318-.844l-3.677.964.984-3.595c-.607-1.052-.927-2.246-.926-3.468.001-3.825 3.113-6.937 6.937-6.937 1.856.001 3.598.723 4.907 2.034 1.31 1.311 2.031 3.054 2.03 4.908-.001 3.825-3.113 6.938-6.937 6.938z" />
            </svg>
            <span className="text-[15px] font-semibold">
              {dictionary?.propertyDetail?.whatsapp || 'WhatsApp'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default PropertyContactInfo
